/**
 *
 * Onboard Azure Account
 *
 */

import React, { memo, useCallback, useReducer } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import {
  Alert,
  AlertIcon,
  Box,
  HStack,
  Input,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { PageHeaderWithIcon } from 'components/DataDisplay';
import {
  defaultFormStyles,
  Form,
  FormSchema,
  textFieldStyles,
} from 'components/DataEntry/Form';
import {
  AzureIcon,
  CheckmarkCircleIcon,
  CopyIcon,
  QuestionCircleIcon,
  ThirdPartyIcon,
} from 'components/Icons';
import { errorToast } from 'components/Toast';
import { selectApp } from 'containers/App/selectors';
import { actions as appActions } from 'containers/App/slice';
import {
  azureHelpHeaderMapping,
  renderHelp,
} from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/MicrosoftAzure/Help/utils';
import { AzureOnboardingHelp } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/MicrosoftAzure/types';
import { renderRequiredSteps } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardGcp/utils';
import { ReadOnlyTag } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/ReadOnlyTag';
import {
  renderRadioButton,
  stopSignWithMessage,
} from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/utils';
import { selectCloudAccountWizard } from 'containers/Setup/CloudAccounts/CloudAccountWizard/selectors';
import { actions } from 'containers/Setup/CloudAccounts/CloudAccountWizard/slice';

import { formStyles, radioBoxStyles } from '../../styles';

interface Props {}

export const OnboardMicrosoftAzureForm = memo((props: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { onboardingAccount } = useSelector(selectCloudAccountWizard);
  const { cloudAccounts } = useSelector(selectApp);
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      name: '',
      accountNameUsedError: '',
      subscriptionId: '',
      applicationId: '',
      tenantId: '',
      secret: '',
      onboardingAccountType: 'Management Group',
      isConfirm: false,
      scriptExecuted: false,
      showHelp: AzureOnboardingHelp.None,
      hasError: false,
    },
  );

  const handleSubmit = data => {
    const {
      accountName,
      secret,
      tenantId,
      subscriptionId,
      applicationId,
    } = data;

    const onError = err => {
      updateState({ hasError: true });
      if (err?.code === 409) {
        updateState({ accountNameUsedError: err.description ?? '-' });
      } else {
        errorToast({ title: err.title, description: err.description });
      }
    };

    dispatch(
      actions.onboardCloudAccount({
        q: {
          cloudAccountCreateRequest: {
            name: accountName,
            cloud_type: CloudProviders.Azure,
            azure: {
              client_credential: secret,
              authority: tenantId,
              subscription_id: subscriptionId,
              client_id: applicationId,
            },
          },
        },
        onSuccess: () => navigate('/setup/accounts'),
        onError,
      }),
    );
  };
  const handleReset = () => {
    navigate('/setup/accounts');
    if (isEmpty(cloudAccounts.data))
      dispatch(appActions.updateShowGettingStarted(true));
  };

  const toggleHelp = useCallback(
    (helpType: AzureOnboardingHelp) => {
      updateState({
        showHelp:
          state.showHelp === helpType ? AzureOnboardingHelp.None : helpType,
      });
    },
    [state.showHelp],
  );

  const steps = [
    {
      label:
        'Execute script to check your permissions and prep Azure subscriptions for onboarding',
      helpHeader: 'Automatically prep Azure Subscription for Onboarding',
      headerIcon: <CheckmarkCircleIcon />,
      iconBgColor: 'green.300',
      helpContent: (
        <Stack spacing={4}>
          <Box fontWeight={600} fontSize="md">
            Execute script to check your permissions and prep Azure
            subscriptions for onboarding
          </Box>
          <Box h={32} bg={customTheme.colors.gray['50']} p={4} borderRadius={6}>
            {'curl -sL https://sm-test-434.s3.us-east-1.amazonaws.com/azure_onboarding.sh | bash -s -- --scope ' +
              state.subscriptionId}
          </Box>
        </Stack>
      ),
      secondary: [
        {
          label: 'Copy script command to execute on Azure CLI',
          icon: <CopyIcon p={0.5} />,
          tooltip: 'Copy command',
          copyText:
            'curl -sL https://sm-test-434.s3.us-east-1.amazonaws.com/azure_onboarding.sh | bash -s -- --scope ' +
            state.subscriptionId,
        },
        {
          label: 'Redirect to Azure console and open Azure CLI',
          icon: <ThirdPartyIcon />,
          url: 'https://portal.azure.com/#cloudshell/',
          tooltip: 'Redirect to Azure console. Manually execute command',
        },
      ],
    },
  ];

  const commonParams = {
    styles: {
      label: {
        ...textFieldStyles.label,
        justifyContent: 'space-between',
      },
    },
    helpIcon: renderHelp(),
    onClickHelpIcon: () => toggleHelp(AzureOnboardingHelp.InputParameters),
  };

  const inputParams: FormSchema = !!state.scriptExecuted
    ? {
        tenantId: {
          type: 'text',
          label: 'Directory (Tenant) ID',
          placeholder: 'abcddf01-2945-6789-0a8c-def089345678',
          value: state.tenantId,
          isRequired: true,
          onChange: value => updateState({ tenantId: value, hasError: false }),
          ...commonParams,
        },
        applicationId: {
          type: 'text',
          label: 'Application (Client) ID',
          placeholder: 'fsdddf01-1245-1289-128c-12308934qasw',
          value: state.applicationId,
          isRequired: true,
          onChange: value =>
            updateState({ applicationId: value, hasError: false }),
          ...commonParams,
        },
        secret: {
          type: 'text',
          label: 'Secret',
          placeholder: '******************',
          value: state.secret,
          htmlInputType: 'password',
          isRequired: true,
          onChange: value => updateState({ secret: value, hasError: false }),
          ...commonParams,
        },
      }
    : {};

  return (
    <>
      <Form
        title="Add Azure Subscription(s)"
        titleIcon={<AzureIcon />}
        isTitleIconFilled={false}
        schema={{
          accountName: {
            type: 'text',
            label: 'Connector Name',
            placeholder: 'Enter connector name',
            isRequired: true,
            error: state.accountNameUsedError,
            onChange: value => {
              updateState({
                name: value,
                accountNameUsedError: value.match(/^[a-zA-Z0-9-]*$/i)
                  ? ''
                  : 'Only Alphanumeric, hyphen is allowed!',
                hasError: false,
              });
            },
            autoFocus: true,
          },
          cfs_template_type_info: {
            type: 'custom-with-form-control',
            label: 'Onboarding mode',
            component: <ReadOnlyTag />,
          },
          role_arn_creation: {
            type: 'custom-with-form-control',
            label: 'I’d like to onboard',
            component: (
              <Stack {...radioBoxStyles} spacing={4}>
                <RadioGroup
                  value={state.onboardingAccountType}
                  onChange={value => {
                    updateState({
                      onboardingAccountType: value,
                      hasError: false,
                    });
                  }}
                  w="full"
                >
                  <HStack spacing={10} w="full">
                    <Box w="full">
                      {renderRadioButton(
                        'Management Group',
                        'All subscriptions',
                        'Management Group',
                      )}
                    </Box>
                    <Box w="full">
                      {renderRadioButton(
                        'Subscription',
                        'Single subscription',
                        'Subscription',
                      )}
                    </Box>
                  </HStack>
                </RadioGroup>
                <Stack>
                  <HStack justify="space-between">
                    <HStack spacing={0}>
                      <Box fontWeight={600}>
                        {state.onboardingAccountType === 'Subscription'
                          ? 'Subscription ID'
                          : 'Management Group ID'}
                      </Box>
                      <Box color="red.400">*</Box>
                    </HStack>
                    <HStack
                      spacing={1}
                      cursor="pointer"
                      onClick={() =>
                        toggleHelp(
                          state.onboardingAccountType === 'Subscription'
                            ? AzureOnboardingHelp.GettingSubscriptionId
                            : AzureOnboardingHelp.GettingManagementGroupId,
                        )
                      }
                    >
                      <QuestionCircleIcon w={4} h={4} />
                      <Box fontSize="sm" color={customTheme.colors.gray['300']}>
                        Where's this?
                      </Box>
                    </HStack>
                  </HStack>
                  <Input
                    {...defaultFormStyles?.textField?.input}
                    onChange={e => {
                      updateState({
                        subscriptionId: e.target.value,
                        isConfirm: false,
                        scriptExecuted: false,
                        hasError: false,
                      });
                    }}
                  />
                </Stack>
              </Stack>
            ),
          },
          confirmMessage: {
            type: 'custom',
            component: () =>
              stopSignWithMessage(
                'Review requirements and execute these steps before proceeding further',
                'Confirm that you have permissions to onboard',
                state.isConfirm,
                val => {
                  updateState({ isConfirm: val });
                },
                () =>
                  toggleHelp(AzureOnboardingHelp.ManagementGroupPermissions),
                !state.subscriptionId,
              ),
          },
          steps: {
            type: 'custom',
            component: () =>
              renderRequiredSteps({
                steps,
                onClickButton: () => toggleHelp(AzureOnboardingHelp.Onboarding),
                copied: state.copied,
                setCopied: val => updateState({ copied: val }),
                isDisabled: !state.subscriptionId || !state.isConfirm,
                isChecked: state.scriptExecuted,
                setIsChecked: val => updateState({ scriptExecuted: val }),
              }),
          },
          ...inputParams,
          ...(state.hasError
            ? {
                validatedMessage: {
                  type: 'custom',
                  component: () => {
                    return (
                      <Box w="full">
                        <Alert
                          status={'error'}
                          borderRadius={6}
                          title={onboardingAccount.error?.title}
                        >
                          <AlertIcon />
                          {onboardingAccount.error?.description}
                        </Alert>
                      </Box>
                    );
                  },
                },
              }
            : {}),
        }}
        helpDrawer={{
          isOpen: state.showHelp !== AzureOnboardingHelp.None,
          onClose: () => updateState({ showHelp: AzureOnboardingHelp.None }),
          content: azureHelpHeaderMapping(state.showHelp, state.subscriptionId)
            ?.content,
          header: (
            <PageHeaderWithIcon
              label={
                azureHelpHeaderMapping(state.showHelp, state.subscriptionId)
                  ?.header
              }
              icon={
                azureHelpHeaderMapping(state.showHelp, state.subscriptionId)
                  ?.icon
              }
              reversed
              fontWeight={600}
              fontSize="lg"
            />
          ),
          styles: { body: { pt: 6 } },
        }}
        buttonOptions={{
          submit: {
            name: 'CONNECT',
            isLoading: onboardingAccount.isLoading,
            isDisabled:
              !state.name ||
              !state.secret ||
              !state.applicationId ||
              !state.subscriptionId ||
              !state.tenantId ||
              !state.name ||
              state.accountNameUsedError ||
              !state.isConfirm ||
              !state.scriptExecuted ||
              onboardingAccount.isLoading,
          },
          reset: {
            isVisible: true,
            onClick: handleReset,
          },
        }}
        styles={formStyles()}
        handleSubmit={handleSubmit}
      />
    </>
  );
});
